import React from 'react';
import useClockTime from '../useClockTime';

const CX = 150, CY = 150;
const TICK_OUTER = 118;
const NUMERAL_R = 100;

const ticks = Array.from({ length: 60 }, (_, i) => {
  const angle = (i * 6 - 90) * Math.PI / 180;
  const isHour = i % 5 === 0;
  const innerR = isHour ? TICK_OUTER - 13 : TICK_OUTER - 6;
  return {
    x1: CX + TICK_OUTER * Math.cos(angle),
    y1: CY + TICK_OUTER * Math.sin(angle),
    x2: CX + innerR * Math.cos(angle),
    y2: CY + innerR * Math.sin(angle),
    strokeWidth: isHour ? 2.5 : 1,
  };
});

const numerals = Array.from({ length: 12 }, (_, i) => {
  const num = i + 1;
  const angle = (num * 30 - 90) * Math.PI / 180;
  return { num, x: CX + NUMERAL_R * Math.cos(angle), y: CY + NUMERAL_R * Math.sin(angle) };
});

const ClassicWood = () => {
  const { hourDeg, minuteDeg, secondDeg } = useClockTime();

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 300, height: 300 }}>
      <svg width={300} height={300} viewBox="0 0 300 300" xmlns="http://www.w3.org/2000/svg">
        <defs>
          {/* Outer wood-tone ring */}
          <radialGradient id="cw-outer" cx="40%" cy="35%">
            <stop offset="0%" stopColor="#5c2e0a" />
            <stop offset="60%" stopColor="#3d1f07" />
            <stop offset="100%" stopColor="#2a1505" />
          </radialGradient>
          {/* Inner brass face */}
          <radialGradient id="cw-face" cx="38%" cy="32%">
            <stop offset="0%" stopColor="#d4940a" />
            <stop offset="50%" stopColor="#c07c08" />
            <stop offset="100%" stopColor="#9e6206" />
          </radialGradient>
          {/* Mid ring (dark separator) */}
          <radialGradient id="cw-mid" cx="50%" cy="50%">
            <stop offset="0%" stopColor="#4a2208" />
            <stop offset="100%" stopColor="#2c1005" />
          </radialGradient>
        </defs>

        {/* Background outer circle (wood frame) */}
        <circle cx={CX} cy={CY} r="148" fill="url(#cw-outer)" />

        {/* Outer highlight ring */}
        <circle cx={CX} cy={CY} r="148" fill="none" stroke="#b8720a" strokeWidth="2" />
        <circle cx={CX} cy={CY} r="145" fill="none" stroke="#6b3a0e" strokeWidth="1" />

        {/* Art Deco geometric border marks */}
        {Array.from({ length: 12 }, (_, i) => {
          const angle = (i * 30 - 90) * Math.PI / 180;
          const r = 140;
          return (
            <rect key={i}
              x={CX + r * Math.cos(angle) - 2}
              y={CY + r * Math.sin(angle) - 6}
              width="4" height="12" rx="1"
              fill="#c8940a"
              transform={`rotate(${i * 30}, ${CX + r * Math.cos(angle)}, ${CY + r * Math.sin(angle)})`}
            />
          );
        })}

        {/* Mid ring (dark separator between wood and brass) */}
        <circle cx={CX} cy={CY} r="131" fill="url(#cw-mid)" />
        <circle cx={CX} cy={CY} r="131" fill="none" stroke="#c8940a" strokeWidth="2.5" />
        <circle cx={CX} cy={CY} r="128" fill="none" stroke="#6b3a0e" strokeWidth="1" />

        {/* Brass face */}
        <circle cx={CX} cy={CY} r="124" fill="url(#cw-face)" />
        <circle cx={CX} cy={CY} r="124" fill="none" stroke="#c8940a" strokeWidth="1.5" />

        {/* Subtle inner ring on face */}
        <circle cx={CX} cy={CY} r="119" fill="none" stroke="#9e6206" strokeWidth="0.8" />

        {/* Tick marks */}
        {ticks.map((t, i) => (
          <line key={i} x1={t.x1} y1={t.y1} x2={t.x2} y2={t.y2}
            stroke="#2a1505" strokeWidth={t.strokeWidth} strokeLinecap="round" />
        ))}

        {/* Numerals */}
        {numerals.map(({ num, x, y }) => (
          <text key={num} x={x} y={y + 4.5} textAnchor="middle"
            fontSize="13" fontFamily="Georgia, 'Times New Roman', serif"
            fill="#2a1505" fontWeight="700">
            {num}
          </text>
        ))}

        {/* Hour hand */}
        <g transform={`rotate(${hourDeg}, ${CX}, ${CY})`}>
          <line x1={CX} y1={CY + 16} x2={CX} y2={CY - 68}
            stroke="#1a0a02" strokeWidth="6" strokeLinecap="round" />
        </g>

        {/* Minute hand */}
        <g transform={`rotate(${minuteDeg}, ${CX}, ${CY})`}>
          <line x1={CX} y1={CY + 18} x2={CX} y2={CY - 90}
            stroke="#1a0a02" strokeWidth="4" strokeLinecap="round" />
        </g>

        {/* Second hand */}
        <g transform={`rotate(${secondDeg}, ${CX}, ${CY})`}>
          <line x1={CX} y1={CY + 22} x2={CX} y2={CY - 100}
            stroke="#c0392b" strokeWidth="1.5" strokeLinecap="round" />
        </g>

        {/* Center cap */}
        <circle cx={CX} cy={CY} r="6.5" fill="#2a1505" />
        <circle cx={CX} cy={CY} r="3" fill="#c8940a" />
      </svg>
    </div>
  );
};

export default ClassicWood;
